// System Design bypass modes — the pathways on which the configurations tab
// is hidden because the app supplies the configuration itself:
//   - 'ducted-only': the strict Traditional Ducted preference (AHA-6681
//     pathway exclusivity) with one existing ducted system on a structure
//     with at most one conditioned level. Systems come straight from
//     Existing Equipment's compatible combinations; Add-Ons stays available
//     and shows the ducted line set, required accessories and add-on
//     categories for them. With two or more conditioned levels, or two or
//     more existing ducted systems, System Design is required instead: each
//     ducted condenser / air-handler pair designed there is linked to an
//     existing system and sized from its own zone (utils/ducted-design).
//   - 'hybrid-whole-house': the Hybrid preference with no mini-split rooms
//     on the structure. A whole-house ducted configuration is generated in
//     the background (HybridWholeHouseConfigurationWatcher); Choose Systems
//     and Add-Ons keep their normal configuration-derived flow.
// null means System Design is a required step — hand-built on ductless and
// hybrid-with-rooms, auto-built one unit per room on Hotel Style PTAC
// (PtacConfigurationWatcher) with the units editable in place.
import { tabGatingMessages } from 'data/constants';
import { ActiveTabType, ExistingSystemType, LevelType } from 'data/types';
import { requiresDuctedSystemDesign } from 'utils/ducted-design-requirement';
import { hasHybridDuctlessSpaces } from 'utils/hybrid-ductless-spaces';
import {
  isDuctedOnlySystemPreference,
  isHybridOnlySystemPreference
} from 'utils/project';

export type SystemDesignBypassMode = 'ducted-only' | 'hybrid-whole-house';

export type TabLock = { message: string; escapeTo: ActiveTabType };

export type TabLocks = Partial<Record<ActiveTabType, TabLock>>;

export type GetSystemDesignBypassModeParams = {
  systemPreference: unknown;
  structure: LevelType[];
  existingSystems: ExistingSystemType[];
  autoLayoutSpaceIds: readonly string[];
};

const CONFIGURATIONS_LOCK_ESCAPE: ActiveTabType = 'structure';

const DUCTED_ONLY_TAB_LOCKS: TabLocks = {
  configurations: {
    message: tabGatingMessages.configurationsLockedDuctedOnly,
    escapeTo: CONFIGURATIONS_LOCK_ESCAPE
  }
};

const HYBRID_WHOLE_HOUSE_TAB_LOCKS: TabLocks = {
  configurations: {
    message: tabGatingMessages.configurationsLockedHybridWholeHouse,
    escapeTo: CONFIGURATIONS_LOCK_ESCAPE
  }
};

const NO_TAB_LOCKS: TabLocks = {};

export function getSystemDesignBypassMode({
  systemPreference,
  structure,
  existingSystems,
  autoLayoutSpaceIds
}: GetSystemDesignBypassModeParams): SystemDesignBypassMode | null {
  if (isDuctedOnlySystemPreference(systemPreference)) {
    return requiresDuctedSystemDesign({ structure, existingSystems })
      ? null
      : 'ducted-only';
  }
  if (
    isHybridOnlySystemPreference(systemPreference) &&
    !hasHybridDuctlessSpaces(structure, autoLayoutSpaceIds)
  ) {
    return 'hybrid-whole-house';
  }
  return null;
}

// The click-block message and the lock watcher's escape target per locked
// tab. Reference-stable per mode so effects can depend on the result.
export function getBypassTabLocks(
  mode: SystemDesignBypassMode | null
): TabLocks {
  switch (mode) {
    case 'ducted-only':
      return DUCTED_ONLY_TAB_LOCKS;
    case 'hybrid-whole-house':
      return HYBRID_WHOLE_HOUSE_TAB_LOCKS;
    default:
      return NO_TAB_LOCKS;
  }
}

export function isSystemDesignLocked(
  mode: SystemDesignBypassMode | null
): boolean {
  return mode !== null;
}
